import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAPI } from '../../hooks/useAPI';
import { uploadImage } from '../../utils/uploadFile';
import Notification from '../../components/Notification';
import ConfirmDialog from '../../components/ConfirmDialog';
import APP_CONFIG from '../../config/appConfig';

const ManageProducts = () => {
  const { data: products, loading, addItem, updateItem, deleteItem } = useAPI('products');
  
  // Ensure products is always an array
  const safeProducts = Array.isArray(products) ? products : [];
  
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    product: null,
    title: '',
    message: '',
    confirmText: 'Delete',
    cancelText: 'Cancel'
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image: null,
    inStock: true,
    featured: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      console.log('📝 Frontend: Starting product submission process');
      console.log('📋 Form data:', {
        name: formData.name,
        price: formData.price,
        category: formData.category,
        description: formData.description?.substring(0, 50) + '...',
        inStock: formData.inStock,
        featured: formData.featured,
        hasImage: !!formData.image
      });

      let imageUrl = '';
      if (formData.image) {
        console.log('📁 Frontend: Uploading new image file');
        imageUrl = await uploadImage(formData.image, 'products');
        console.log('✅ Frontend: New image uploaded successfully:', imageUrl);
      } else if (editingProduct && editingProduct.image) {
        console.log('📁 Frontend: Keeping existing image:', editingProduct.image);
        imageUrl = editingProduct.image; // Keep existing image if no new one uploaded
      }

      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        image: imageUrl,
        createdAt: editingProduct ? editingProduct.createdAt : new Date() // Keep original creation date when editing
      };

      console.log('💾 Frontend: Preparing product data for API call:', {
        name: productData.name,
        price: productData.price,
        category: productData.category,
        description: productData.description?.substring(0, 50) + '...',
        inStock: productData.inStock,
        featured: productData.featured,
        hasImage: !!productData.image
      });

      if (editingProduct) {
        console.log('✏️ Frontend: Updating existing product');
        console.log('🆔 Editing product ID:', editingProduct.id || editingProduct._id);
        const productId = editingProduct.id || editingProduct._id;
        await updateItem(productId, productData);
      } else {
        console.log('➕ Frontend: Creating new product');
        await addItem(productData);
      }

      console.log('✅ Frontend: Product operation completed successfully');
      setNotification({ message: editingProduct ? 'Product updated successfully!' : 'Product added successfully!', type: 'success' });
      setFormData({ name: '', description: '', price: '', category: '', image: null, inStock: true, featured: false });
      setShowForm(false);
      setEditingProduct(null);
    } catch (error) {
      console.error('❌ Frontend: Error saving product:', error);
      setNotification({ message: 'Failed to save product. Please try again.', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (product) => {
    console.log('✏️ Frontend: Starting edit mode for product:', product);
    console.log('📋 Product data being loaded:', {
      name: product.name,
      price: product.price,
      category: product.category,
      description: product.description?.substring(0, 50) + '...',
      inStock: product.inStock,
      featured: product.featured,
      hasImage: !!product.image
    });
    
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      image: null,
      inStock: product.inStock,
      featured: product.featured
    });
    setShowForm(true);
  };

  const handleDelete = async (product) => {
    console.log('🗑️ Frontend: Delete request for product:', product.name);
    setConfirmDialog({
      isOpen: true,
      product: product,
      title: 'Delete Product',
      message: `Are you sure you want to delete "${product.name}"? This action cannot be undone.`
    });
  };

  const confirmDelete = async () => {
    try {
      console.log('🗑️ Delete: Starting delete process');
      console.log('📋 Product to delete:', confirmDialog.product);
      console.log('🆔 Product ID:', confirmDialog.product.id || confirmDialog.product._id);
      const productId = confirmDialog.product.id || confirmDialog.product._id;
      await deleteItem(productId);
      console.log('✅ Delete: Product deleted successfully');
      setNotification({ message: 'Product deleted successfully!', type: 'success' });
    } catch (error) {
      console.error('❌ Delete: Error deleting product:', error);
      setNotification({ message: 'Failed to delete product. Please try again.', type: 'error' });
    } finally {
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  const cancelDelete = () => {
    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  // Helper function to get image URL
  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    const baseServerUrl = APP_CONFIG.getBaseServerUrl();
    return `${baseServerUrl}${image}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading products...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Manage Products</h1>
              <p className="mt-2 text-gray-600">Create and manage your product catalog</p>
            </div>
            <button
              onClick={() => {
                setShowForm(true);
                setEditingProduct(null);
                setFormData({ name: '', description: '', price: '', category: '', image: null, inStock: true, featured: false });
              }}
              className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
            >
              Add New Product
            </button>
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter product name..."
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price ($)</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Select Category</option>
                  <option value="Art">Art</option>
                  <option value="Books">Books</option>
                  <option value="Home Decor">Home Decor</option>
                  <option value="Stationery">Stationery</option>
                  <option value="Gifts">Gifts</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Describe your product..."
                  required
                />
              </div>

              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="inStock"
                    checked={formData.inStock}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">In Stock</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Featured Product</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {editingProduct && editingProduct.image && !formData.image && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 mb-2">Current image:</p>
                    <img
                      src={getImageUrl(editingProduct.image)}
                      alt={editingProduct.name}
                      className="w-32 h-32 object-cover rounded-lg"
                    />
                  </div>
                )}
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : (editingProduct ? 'Update Product' : 'Create Product')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingProduct(null);
                    setFormData({ name: '', description: '', price: '', category: '', image: null, inStock: true, featured: false });
                  }}
                  className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Products List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Products ({safeProducts.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {safeProducts.map((product) => (
                  <tr key={product.id || product._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {product.image && (
                          <img
                            src={getImageUrl(product.image)}
                            alt={product.name}
                            className="h-12 w-12 rounded-lg object-cover mr-4"
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {product.description?.substring(0, 50)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                        {product.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${product.price?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          product.inStock 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {product.inStock ? 'In Stock' : 'Out of Stock'}
                        </span>
                        {product.featured && (
                          <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                            Featured
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(product)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        {/* Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
        />
      </div>
    </div>
  );
};

export default ManageProducts;